// <s-welcome-popup> — Stormi Club welcome popup.
// Opens a native <dialog> after 1 minute of browsing for visitors who have not subscribed or recently closed it.
// The signup itself is a normal Shopify customer form; after the page reloads with the result,
// the popup reopens to show the success (discount code) or the error message.
// Add ?stormi-club to any storefront URL to open it immediately (handy for previews and links).
const SUBSCRIBED_KEY = 'stormi-club-subscribed';
const DISMISSED_KEY = 'stormi-club-dismissed-at';
const SUBMITTED_KEY = 'stormi-club-submitted';
const TIME_KEY = 'stormi-club-browsing-ms';
const DISMISS_DAYS = 14;

class SWelcomePopup extends HTMLElement {
  #ac;
  #timer;
  #ticker;
  #dialog;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.#dialog.close();
    }, opts);

    this.#dialog.addEventListener('close', () => {
      if (this.#state() !== 'success') this.#store(localStorage, DISMISSED_KEY, String(Date.now()));
    }, opts);

    this.querySelector('form')?.addEventListener('submit', () => {
      this.#store(sessionStorage, SUBMITTED_KEY, '1');
    }, opts);

    const state = this.#state();
    const submittedHere = this.#read(sessionStorage, SUBMITTED_KEY) === '1';

    if (submittedHere && (state === 'success' || state === 'error')) {
      this.#remove(sessionStorage, SUBMITTED_KEY);
      if (state === 'success') this.#store(localStorage, SUBSCRIBED_KEY, '1');
      this.#open();
      return;
    }

    if (new URLSearchParams(window.location.search).has('stormi-club')) {
      this.#open();
      return;
    }

    if (this.hasAttribute('data-skip') || this.#read(localStorage, SUBSCRIBED_KEY) === '1') return;

    const dismissedAt = Number(this.#read(localStorage, DISMISSED_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000) return;

    // Count time actually spent browsing (tab visible), carried across page changes in this visit.
    const delay = Number.parseInt(this.dataset.delay || '60000', 10);
    const tick = 1000;
    this.#ticker = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      const spent = Number(this.#read(sessionStorage, TIME_KEY) || 0) + tick;
      this.#store(sessionStorage, TIME_KEY, String(spent));
      if (spent >= delay) {
        window.clearInterval(this.#ticker);
        this.#openWhenFree();
      }
    }, tick);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    window.clearTimeout(this.#timer);
    window.clearInterval(this.#ticker);
  }

  #state() {
    return this.querySelector('[data-popup-state]')?.dataset.popupState || 'idle';
  }

  // Wait politely if another dialog (cart, search, menu) is open.
  #openWhenFree(attempt = 0) {
    const otherOpen = document.querySelector('dialog[open]');
    if (otherOpen && otherOpen !== this.#dialog) {
      if (attempt < 5) this.#timer = window.setTimeout(() => this.#openWhenFree(attempt + 1), 5000);
      return;
    }
    this.#open();
  }

  #open() {
    if (!this.#dialog.open) this.#dialog.showModal();
  }

  #read(storage, key) {
    try { return storage.getItem(key); } catch { return null; }
  }

  #store(storage, key, value) {
    try { storage.setItem(key, value); } catch { /* storage unavailable */ }
  }

  #remove(storage, key) {
    try { storage.removeItem(key); } catch { /* storage unavailable */ }
  }
}

if (!customElements.get('s-welcome-popup')) {
  customElements.define('s-welcome-popup', SWelcomePopup);
}
